import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useToasts } from 'react-toast-notifications';

import Modal from '../modal';

import MapController from '../../../controllers/MapController';

import config from '../../../config/config';

import { storeTempGraphic, toggleLayerModal } from '../../../reducers/mapview/actions';
import { toggleMapLoader } from '../../../reducers/mapview/actions';

import './layer-modal.scss';

const LayerModal = (props: { layerTitle: string | null; tempGraphic: __esri.Graphic | null; deleteLayer: boolean }) => {
  const { layerTitle, tempGraphic, deleteLayer } = props;

  // LOCAL STATE
  const [layerName, setLayerName] = useState<string>(layerTitle ? layerTitle : '');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const dispatch = useDispatch();
  const { addToast } = useToasts();

  // Event Listeners don't have access to latest state, so have to store it in a ref so the EventListner can access it
  const layerNameRef = useRef(layerName);
  const errorMessageRef = useRef(errorMessage);
  const maxLayerTitleLengthRef = useRef(config.esri.maxLayerTitleLength);
  const handleSetLayerName = (name: string) => {
    layerNameRef.current = name;
    setLayerName(name);
  };

  // Because Event Listener can't access latest state, we have to pass layerName to saveAndClose so hitting enter works AND hitting the save button works too
  const saveAndClose = (layerName: string) => {
    let successMessage: string = '';
    if (layerTitle) {
      const layerToEdit = MapController._myLayers.find((layer) => layer.title === layerTitle);
      if (layerToEdit) {
        layerToEdit.title = layerName;
        successMessage = 'Layer updated successfully!';
        addToast(successMessage, { appearance: 'success', autoDismiss: true });
      }
    } else {
      if (tempGraphic) {
        if (tempGraphic.attributes) {
          tempGraphic.attributes.title = layerName;
        }
        MapController.createNewLayer(tempGraphic)
          .then((res: any) => {
            successMessage = res;
            const id = tempGraphic.attributes.id;
            if (id.includes('-shapefile')) {
              successMessage = 'Layer successfully uploaded!';
            } else if (id.includes('-merge')) {
              successMessage = 'Selected Layers have been merged!';
            } else if (id.includes('-intersect')) {
              successMessage = 'Intersection layer created!';
            } else if (id.includes('-contextual')) {
              successMessage = 'Contextual layer created!';
            }
            dispatch(toggleMapLoader(false));
            dispatch(storeTempGraphic(null));
            addToast(successMessage, { appearance: 'success', autoDismiss: true });
          })
          .catch((err: any) => {
            addToast(err, { appearance: 'error', autoDismiss: true });
          });
      }
    }
    dispatch(toggleLayerModal(false));
  };

  const cancel = () => {
    if (tempGraphic && tempGraphic.attributes.id.includes('-contextual')) {
      MapController._contextualLayer?.remove(tempGraphic);
    }
    addToast(`Layer ${layerTitle ? 'update' : 'creation'} cancelled`, { appearance: 'error', autoDismiss: true });
    dispatch(storeTempGraphic(null));
    dispatch(toggleLayerModal(false));
    dispatch(toggleMapLoader(false));
  };

  const confirmDeleteLayer = () => {
    const layerToDelete = MapController._myLayers.find((layer) => layer.title === layerTitle);
    MapController.deleteLayer(layerToDelete.id);
    addToast('Layer deleted', { appearance: 'success', autoDismiss: true });
    dispatch(toggleLayerModal(false));
  };

  const validateName = () => {
    const validated = MapController.checkValidLayerTitle(layerName.trim());
    if (validated !== layerName.trim()) {
      errorMessageRef.current = validated;
      setErrorMessage(validated);
    } else {
      errorMessageRef.current = '';
      setErrorMessage('');
    }
  };

  const keyEventListener = (e: KeyboardEvent) => {
    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      e.preventDefault();
      if (errorMessageRef.current !== '' || !layerNameRef || layerNameRef.current === '') {
        return;
      } else {
        if (deleteLayer) {
          confirmDeleteLayer();
        } else {
          saveAndClose(layerNameRef.current);
        }
      }
    }
    if (e.code === 'Escape') {
      e.preventDefault();
      cancel();
    }
    // We want to stop a user being able to type more than maxLayerTitleLength but ensure backspace and delete buttons always work
    if (layerNameRef.current.length > maxLayerTitleLengthRef.current && e.code !== 'Delete' && e.code !== 'Backspace') {
      e.preventDefault();
      return;
    }
  };

  // custom useEffect that doesn't fire on mount
  const useLazyEffect: typeof useEffect = (cb, dep) => {
    const initializeRef = useRef<boolean>(false);

    useEffect((...args) => {
      if (initializeRef.current) {
        cb(...args);
      } else {
        initializeRef.current = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dep);
  };

  useLazyEffect(validateName, [layerName]);

  useEffect(() => {
    document.addEventListener('keydown', keyEventListener);

    // componentWillUnmount
    return () => {
      document.removeEventListener('keydown', keyEventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal>
      <div className={`name-modal ${deleteLayer ? 'delete' : ''}`}>
        <button className='close esri-icon-close' onClick={() => cancel()} />
        {deleteLayer ? (
          <React.Fragment>
            <div className='delete-layer-text'>Are you sure you want to delete this layer?</div>
            <div className='button-wrapper'>
              <button onClick={() => confirmDeleteLayer()}>Delete</button>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className='title'>{layerTitle ? 'Rename' : 'Name'} Map Layer</div>
            <input
              autoFocus
              type='text'
              placeholder='Enter layer name...'
              value={layerName}
              onChange={(e) => handleSetLayerName(e.target.value)}
            />
            <div className={`error-message ${errorMessage === '' ? 'hidden' : ''}`}>{errorMessage}</div>
            <div className='button-wrapper'>
              <button onClick={() => saveAndClose(layerName)} disabled={layerName === '' || errorMessage !== ''}>
                Save & Close
              </button>
            </div>
          </React.Fragment>
        )}
      </div>
    </Modal>
  );
};

export default LayerModal;
